import React, { useState, useEffect, useRef } from 'react';
import { printHtmlSilently } from '../lib/silentPrint';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Search, Printer, Package, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Date().toISOString().substring(0, 10);
const offsetStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().substring(0, 10);
};
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().substring(0, 10);
};
const firstOfYear = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().substring(0, 10);
};

const fmtDateBR = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
};

export const ProductSalesReport = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const suggestBoxRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`, { withCredentials: true });
        setProducts(data);
      } catch (err) {
        console.error('Erro ao carregar produtos:', err);
      }
    })();
  }, []);

  // Fecha o box de sugestões ao clicar fora
  useEffect(() => {
    const onClickOutside = (e) => {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filteredProducts = productSearch.trim()
    ? products.filter(p =>
        p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.code === productSearch ||
        p.barcode === productSearch
      ).slice(0, 20)
    : [];

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setProductSearch(p.name);
    setShowSuggestions(false);
  };

  const clearProduct = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setReport(null);
  };

  const applyPreset = (preset) => {
    const today = todayStr();
    if (preset === 'today') { setStart(today); setEnd(today); }
    else if (preset === 'yesterday') {
      const y = offsetStr(-1);
      setStart(y); setEnd(y);
    }
    else if (preset === '7d') { setStart(offsetStr(-6)); setEnd(today); }
    else if (preset === 'month') { setStart(firstOfMonth()); setEnd(today); }
    else if (preset === 'year') { setStart(firstOfYear()); setEnd(today); }
  };

  const handleSearch = async () => {
    if (!selectedProduct) {
      setError('Selecione um produto');
      return;
    }
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const params = new URLSearchParams({
        product_id: selectedProduct.id,
        start_date: start,
        end_date: end,
      });
      const { data } = await axios.get(`${API}/sales/product-report?${params.toString()}`, { withCredentials: true });
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!report) return;
    const html = buildReceiptHtml(report);
    printHtmlSilently(html);
  };

  const buildReceiptHtml = (r) => {
    const now = new Date();
    const nowStr = now.toLocaleString('pt-BR');
    return `<!doctype html><html><head><meta charset="utf-8"><title>Relatório de Produto</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 0; color: #000; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: 700; }
  .row { display: flex; justify-content: space-between; }
  hr { border: none; border-top: 1px dashed #000; margin: 4px 0; }
  h1 { font-size: 13px; margin: 2px 0; text-transform: uppercase; }
  .big { font-size: 13px; font-weight: 700; }
  .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #333; }
</style>
</head><body>
  <div class="center bold">================================</div>
  <div class="center bold">   RELATORIO DE PRODUTO</div>
  <div class="center bold">================================</div>
  <div><span class="lbl">Produto:</span> <span class="bold">${escapeHtml((r.product.name || '').toUpperCase())}</span></div>
  ${r.product.code ? `<div><span class="lbl">Codigo:</span> ${escapeHtml(r.product.code)}</div>` : ''}
  ${r.product.category ? `<div><span class="lbl">Categoria:</span> ${escapeHtml(r.product.category)}</div>` : ''}
  <div><span class="lbl">Periodo:</span> ${fmtDateBR(r.period.start_date)} a ${fmtDateBR(r.period.end_date)}</div>
  <div><span class="lbl">Emitido:</span> ${nowStr}</div>
  <hr/>
  <div class="row"><span>Quantidade total:</span><span class="bold">${formatQty(r.total_quantity, r.product.unit)}</span></div>
  <div class="row"><span>Nº de vendas:</span><span class="bold">${r.sales_count}</span></div>
  <div class="row big"><span>VALOR TOTAL:</span><span>R$ ${formatMoney(r.total_value)}</span></div>
  <hr/>
  <div class="row"><span>Ticket medio:</span><span>R$ ${formatMoney(r.avg_ticket)}</span></div>
  <div class="row"><span>Preco medio:</span><span>R$ ${formatMoney(r.avg_unit_price)}/${r.product.unit || 'un'}</span></div>
  <hr/>
  <div class="center bold">================================</div>
  <div class="center">Smart Flow POS</div>
  <br/><br/>
</body></html>`;
  };

  return (
    <div data-testid="product-report">
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Relatório por Produto</h2>

      {/* Seleção do Produto */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">
          <Package size={12} className="inline mr-1" /> Produto
        </Label>
        <div className="relative mt-1" ref={suggestBoxRef}>
          <div className="flex items-center gap-2">
            <Search size={16} className="absolute left-3 text-slate-400 pointer-events-none" />
            <Input
              value={productSearch}
              onChange={(e) => {
                setProductSearch(e.target.value);
                setSelectedProduct(null);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Digite o nome do produto (ex: moranguinho, picanha)..."
              className="pl-10"
              data-testid="product-search-input"
            />
            {selectedProduct && (
              <Button variant="outline" size="sm" onClick={clearProduct} data-testid="clear-product">
                <X size={14} />
              </Button>
            )}
          </div>
          {showSuggestions && filteredProducts.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-64 overflow-auto" data-testid="product-suggestions">
              {filteredProducts.map(p => (
                <button
                  key={p.id}
                  onClick={() => selectProduct(p)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 text-sm"
                  data-testid={`product-option-${p.id}`}
                >
                  <span className="font-medium">{p.name}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    {p.category && `[${p.category}]`} R$ {Number(p.price || 0).toFixed(2)}/{p.unit || 'un'}
                  </span>
                </button>
              ))}
            </div>
          )}
          {selectedProduct && (
            <div className="mt-2 text-xs text-emerald-700 font-medium">
              ✓ Selecionado: <strong>{selectedProduct.name}</strong> ({selectedProduct.unit || 'un'})
            </div>
          )}
        </div>
      </div>

      {/* Período */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Período</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => applyPreset('today')} data-testid="preset-today">Hoje</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('yesterday')} data-testid="preset-yesterday">Ontem</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('7d')} data-testid="preset-7d">Últimos 7 dias</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('month')} data-testid="preset-month">Este mês</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('year')} data-testid="preset-year">Este ano</Button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs text-slate-500">De</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="date-start"/>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Até</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="date-end"/>
          </div>
        </div>
        <Button
          onClick={handleSearch}
          disabled={loading || !selectedProduct}
          className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
          data-testid="search-report-btn"
        >
          {loading ? 'Buscando...' : 'Gerar Relatório'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Resultado */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="report-result">
          <div className="mb-4 pb-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Produto</p>
            <h3 className="text-2xl font-black text-slate-900">{report.product.name}</h3>
            <p className="text-sm text-slate-600 mt-1">
              Período: <strong>{fmtDateBR(report.period.start_date)}</strong> até <strong>{fmtDateBR(report.period.end_date)}</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Quantidade Total</p>
              <p className="text-3xl font-black text-emerald-700 mt-1" data-testid="result-quantity">
                {formatQty(report.total_quantity, report.product.unit)}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-blue-700">Nº de Vendas</p>
              <p className="text-3xl font-black text-blue-700 mt-1" data-testid="result-sales-count">
                {report.sales_count}
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-yellow-700">Valor Total</p>
              <p className="text-3xl font-black text-yellow-700 mt-1" data-testid="result-total-value">
                R$ {formatMoney(report.total_value)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Ticket médio</p>
              <p className="text-xl font-bold text-slate-800 mt-1">R$ {formatMoney(report.avg_ticket)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Preço médio</p>
              <p className="text-xl font-bold text-slate-800 mt-1">R$ {formatMoney(report.avg_unit_price)}/{report.product.unit || 'un'}</p>
            </div>
          </div>

          <Button
            onClick={handlePrint}
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="print-report-btn"
          >
            <Printer size={16} className="mr-2" />
            Imprimir Cupom (80mm)
          </Button>

          {report.sales_count === 0 && (
            <Alert className="mt-4 bg-yellow-50 border-yellow-300">
              <AlertDescription className="text-yellow-900">
                Nenhuma venda deste produto encontrada no período selecionado.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

// helpers
const formatMoney = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const formatQty = (v, unit) => {
  const n = Number(v || 0);
  const u = unit || 'un';
  if (u.toLowerCase().includes('kg')) return `${n.toFixed(3).replace('.', ',')} kg`;
  return `${(Number.isInteger(n) ? n : n.toFixed(3)).toString().replace('.', ',')} ${u}`;
};
const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
